/*
<purpose>
  Chatbot-Testseite brand preview card — shape C. Renders three states:
  - Seeded:        labeled fields + exact override swatches (Primary/Accent) + Schrift.
  - Auto + cache:  labeled fields + the REAL auto-detected palette (swatches + Schrift + "erkannt am ...").
  - Auto + no cache: labeled fields + honest "noch nicht erfasst" banner (no visit captured yet).
  Falls back to "Brand nicht verfügbar" on null getBrand response.
</purpose>
<dependencies>getBrand / getBrandDetected / BrandConfig / DetectedBrand (services/api), theme (../theme)</dependencies>
<constraints>
  - Card chrome uses theme tokens only; swatch colours come from the customer's brand (getBrand seeded,
    or getBrandDetected for the live auto palette) — never theme tokens.
  - Auto WITHOUT a cached detection renders NO hex (navy is the chatbot's runtime default, not a brand).
    Auto WITH a cached detection renders the detected hex — that IS the customer's real brand.
  - Layout-stable: minHeight pre-allocated to the tallest (seeded/auto-cache) state; content fades in
    once BOTH fetches resolve (no second shift when the detected palette lands).
</constraints>
*/

import React, { useEffect, useState } from 'react';
import { theme } from '../theme';
import { getBrand, getBrandDetected } from '../services/api';
import { BrandConfig, DetectedBrand } from '../serverTypes';

// primaryColor set ⇒ seeded (colour group is all-or-nothing, CI-enforced).
const isSeeded = (b: BrandConfig): boolean => !!b.primaryColor;

const fontLabel = (font: string | null): string =>
  font ? font.split(',')[0].replace(/['"]/g, '').trim() : 'Standard (Poppins)';

const formatDetectedAt = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Shared uppercase mini-label style (matches MetaRow labels in ChatbotTestSeite).
const miniLabel: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: theme.colors.navyMuted,
  fontWeight: 600,
};

const Swatch: React.FC<{ label: string; hex: string }> = ({ label, hex }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.navy }}>
    <span aria-hidden style={{
      width: 13, height: 13, borderRadius: 3, background: hex,
      border: `1px solid ${theme.colors.borderLight}`, display: 'inline-block',
    }} />
    {label} <code style={{ fontFamily: 'monospace' }}>{hex}</code>
  </span>
);

const SchriftRow: React.FC<{ font: string | null }> = ({ font }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 9 }}>
    <span style={miniLabel}>Schrift</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navy }}>{fontLabel(font)}</span>
  </div>
);

// Titel / Begrüßung / Branding — identical across states.
const SharedFields: React.FC<{ b: BrandConfig }> = ({ b }) => (
  <>
    <div style={miniLabel}>Titel</div>
    <div style={{ fontSize: 15, fontWeight: 600, color: theme.colors.navy, margin: '1px 0 12px' }}>
      {b.title}
    </div>
    <div style={miniLabel}>Begrüßung</div>
    <div style={{ fontSize: 13, color: theme.colors.navy, lineHeight: 1.45, marginTop: 1 }}>
      {b.greeting}
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
      <span style={miniLabel}>Branding</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navy }}>
        {b.showBranding ? 'an' : 'aus'}
      </span>
    </div>
  </>
);

// Auto WITH a cached detection — the real brand the live embed wears.
const DetectedState: React.FC<{ d: DetectedBrand }> = ({ d }) => (
  <div style={{ borderTop: `1px solid ${theme.colors.borderLight}`, marginTop: 16, paddingTop: 13 }}>
    <div style={{ ...miniLabel, marginBottom: 7 }}>Automatisch erkannt</div>
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      <Swatch label="Primary" hex={d.primaryColor} />
      <Swatch label="Accent" hex={d.accentColor} />
    </div>
    <SchriftRow font={d.font} />
    <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginTop: 9 }}>
      Erkannt am {formatDetectedAt(d.detectedAt)} aus der Website der Kundin (AA-korrigiert).
    </div>
  </div>
);

// Auto WITHOUT a detection yet — honest placeholder.
const NotYetState: React.FC = () => (
  <div style={{
    background: theme.colors.bgPage,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.borderRadius.md,
    padding: '10px 12px',
    marginTop: 16,
    fontSize: 11.5,
    color: theme.colors.textSecondary,
    lineHeight: 1.5,
  }}>
    <span style={{ color: theme.colors.navy, fontWeight: 600 }}>
      <span aria-hidden style={{
        display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
        background: theme.colors.navyMid, marginRight: 5,
      }} />
      Auto-adaptiv
    </span>
    <br />
    Noch nicht erfasst — sobald das Live-Embed auf der Website der Kundin geladen wird, werden die
    Markenfarben erkannt, AA-korrigiert und hier angezeigt.
  </div>
);

const AutoState: React.FC<{ b: BrandConfig; detected: DetectedBrand | null }> = ({ b, detected }) => (
  <div>
    <SharedFields b={b} />
    {detected ? <DetectedState d={detected} /> : <NotYetState />}
  </div>
);

const SeededState: React.FC<{ b: BrandConfig }> = ({ b }) => (
  <div>
    <SharedFields b={b} />
    <div style={{ borderTop: `1px solid ${theme.colors.borderLight}`, marginTop: 16, paddingTop: 13 }}>
      <div style={{ ...miniLabel, marginBottom: 7 }}>Farben</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Swatch label="Primary" hex={b.primaryColor!} />
        <Swatch label="Accent" hex={b.accentColor!} />
      </div>
      <SchriftRow font={b.font} />
    </div>
  </div>
);

const BrandPreviewCard: React.FC = () => {
  // undefined = loading; null = failed; value = loaded
  const [brand, setBrand] = useState<BrandConfig | null | undefined>(undefined);
  const [detected, setDetected] = useState<DetectedBrand | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    getBrand().then(b => { if (alive) setBrand(b); });
    getBrandDetected().then(d => { if (alive) setDetected(d); });
    return () => { alive = false; };
  }, []);

  const resolved = brand !== undefined && detected !== undefined;

  const cardStyle: React.CSSProperties = {
    background: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.card,
    padding: theme.spacing.lg,
    // Reserve the tallest state's height so neither state shifts the metadata card on load.
    minHeight: 360,
  };
  // Fade content in once BOTH fetches resolve. Reduced-motion handled by global CSS.
  const contentStyle: React.CSSProperties = {
    opacity: resolved ? 1 : 0,
    transition: 'opacity 0.2s ease',
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.colors.navy, marginBottom: theme.spacing.md }}>
        Mein Brand
      </h2>
      <div style={contentStyle}>
        {brand === null && (
          <div style={{ fontSize: 12, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
            Brand nicht verfügbar.
          </div>
        )}
        {brand && isSeeded(brand) && <SeededState b={brand} />}
        {brand && !isSeeded(brand) && <AutoState b={brand} detected={detected ?? null} />}
      </div>
    </div>
  );
};

export default BrandPreviewCard;
